import React from "react";
import styled from "styled-components";

import { Button, ControlGroup, NumericInput, Position, Tooltip } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { CourseWrapper } from "common/entities/courseWrapper/CourseWrapper";
import { FlexJustify } from "common/fragments/Flex";
import VerticalCenter from "common/fragments/VerticalCenter";
import { BlockVariant, BlockView } from "common/views/block/BlockView.view";
import { ButtonVariant, ButtonView } from "common/views/button/Button.view";
import { CardField, CardVariant, CardView } from "common/views/card/Card.view";
import HeadingView, { HeadingVariant } from "common/views/heading/Heading.view";
import IconView from "common/views/icon/Icon.view";
import { ImageVariant, ImageView } from "common/views/image/Image.view";
import PageLinkView from "common/views/pageLink/PageLink.view";
import ProgressBarView from "common/views/progressBar/ProgressBar.view";
import Label from "core/components/label/Label";
import TokenSession from "core/util/session/TokenSession";
import { CourseComponentConfig } from "./CourseComponentConfig";
import DiscountDetails from "./DiscountDetails";
import ServiceRegistry from "core/util/ServiceRegistry";
import ILocalCacheService from "core/service/cache/ILocalCacheService";


interface ICourseComponentProps {
	config: CourseComponentConfig;
	course: CourseWrapper;
	addToCart?: any;
	enableBundlePurchase:Boolean;
	showPrice?:Boolean;
}

// TODO make fragment provider for course fields.

const StyledThumbnailOverlay = styled(VerticalCenter)`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	transition: 0.1s;
	opacity: 0;
	color: #fff;
	cursor: pointer;
	&:hover {
		background-color: rgba(0,0,0,0.4);
		opacity: 1;
	}
`;

const quantityMap = new Map();
export const CourseComponent: React.SFC<ICourseComponentProps> = ({ course, config, addToCart, enableBundlePurchase, showPrice = {} }) => {

	return (
		<CardView variant={CardVariant.SURFACE} viewKey="course-card">
				<div style={{position: "relative"}}>
					<ImageView
						style={{
							display: 'block',
							minHeight: config.thumbnailSize || '150px',
							maxWidth: '100%',
							width: '100%'
						}}
						src={course.getIconUrl()}
						variant={ImageVariant.THUMBNAIL}
						viewKey="course-thumbnail"
					/>
					{
						course.isLaunchable() && (
							<StyledThumbnailOverlay onClick={() => course.launchCourse()}>
								<IconView icon="play" viewKey="course-play-icon" />
							</StyledThumbnailOverlay>
						)
					}
				</div>
				<BlockView variant={BlockVariant.CARD_CONTENT} viewKey="card-primary">
					<CardField key="title">
						<HeadingView viewKey="course-title" children={course.getTitle()} />
					</CardField>
					{
						course.getSubtitle() && (
							<CardField key="subtitle">
								<HeadingView variant={HeadingVariant.H5} viewKey="course-subtitle" children={course.getSubtitle()} />
							</CardField>
						)
					}
					{
						config.showProgress && (
							<CardField key="progress">
								<ProgressBarView value={course.getProgressPercent()} viewKey="course-progress" />
							</CardField>
						)
					}
				</BlockView>
				<BlockView variant={BlockVariant.CARD_ACTIONS} viewKey="card-actions">
					{
					(!(enableBundlePurchase && TokenSession.hasRoles('ROLE_ORG_ADMIN'))) && course.isLaunchable() && (
							<ButtonView onClick={() => course.launchCourse()} variant={ButtonVariant.CARD_ACTION} viewKey="course-launch">
								<Label k="course.launch" v="Launch" />
							</ButtonView>
						)
					}
					{
						showPrice && enableBundlePurchase && TokenSession.hasRoles('ROLE_ORG_ADMIN') && (
							<HeadingView viewKey="entry-price" variant={HeadingVariant.H6}>	
									{course.getEntry().variants[0].price} 
									{ServiceRegistry.get(ILocalCacheService).getString("paymentType")==="entryvar.credits"?" Credits / Seat":""}							
							</HeadingView>
						)
					}

					{
						enableBundlePurchase && TokenSession.hasRoles('ROLE_ORG_ADMIN') &&
						(			
							<ControlGroup>
								<FlexJustify >
									<Tooltip content={<DiscountDetails priceDetails={course.getGroupDiscount()}
										position={Position.BOTTOM} />}>
										<Button icon={IconNames.INFO_SIGN} />
									</Tooltip>
									<NumericInput size={1} min={1} key={course.getEntry().id} defaultValue={'1'} value={quantityMap.get(course.getEntry().id) || 1}
										onValueChange={(e) => handleInputChange(e, course.getEntry().id)} />
									<ButtonView onClick={() => addToCart(course.getEntry().id, course.getTitle(), quantityMap.get(course.getEntry().id))}
										variant={ButtonVariant.PRIMARY} viewKey="review">
										<Label k="course.Add" v="Add" />
									</ButtonView>
								</FlexJustify>
							</ControlGroup>
						)
					}
					{
						! (enableBundlePurchase &&  TokenSession.hasRoles('ROLE_ORG_ADMIN'))
						&& config.enableViewDetailsLink && (
							<PageLinkView params={{productId: course.getItemId()}} viewKey="course-details-link">
								<ButtonView variant={ButtonVariant.CARD_ACTION} viewKey="course-details">
									<Label k="course.view" v="View" />
								</ButtonView>
							</PageLinkView>
						)
					}
				</BlockView>
			</CardView>
	);
};

const handleInputChange = (e, entryId) => {
	quantityMap.set(entryId, e.valueOf());
};



// WEBPACK FOOTER //
// ./src/panels/bundleEntries/component/CourseComponent.tsx